import { SWAGMAGIC_URL } from "@stadium/helpers/app-routes";

import { LinkWithLangType } from "@stadium/shared/src/constants/stores/routes";
import { PROMOTIONAL_STORE_NUMBER, PROMOTIONAL_STORE_SLUG } from "@constants/env-variables";

export interface FooterLink {
  text: string;
  url?: string;
  code?: string;
  linkType?: LinkWithLangType;
  otherUrl?: string;
  openInNewTab?: boolean;
  action?: "open_cookie_preferences" | "open_help_messenger";
  noUrl?: {
    dataAttribute?: { key?: string; value?: string };
    preventDefaultClick?: boolean;
    target?: string;
  };
}

export interface FooterColumn {
  title: string;
  links: FooterLink[];
}

export interface SocialLink {
  platform: string;
  url: string;
  iconUrl: string;
}

export const DEFAULT_COLUMN_1: FooterColumn = {
  title: "The Basics",
  links: [
    { text: "Surprise Your Recipients", url: `${SWAGMAGIC_URL}/surprise-your-recipients` },
    {
      text: "Product & Pricing",
      url: `${SWAGMAGIC_URL}/go/pricing`,
    },
    { text: "Customization", url: `${SWAGMAGIC_URL}/swag-and-custom-branding` },
    {
      text: "FAQ",
      url: "https://help.bystadium.com/hc/en-us",
    },
    { text: "Talk to Sales", url: `${SWAGMAGIC_URL}/go/book-a-call` },
    {
      text: "Get a Quote",
      url: "https://go.bystadium.com/welcome",
    },
    {
      text: "Contact Us",
      url: "https://help.bystadium.com/hc/en-us/requests/new",
    },
  ],
};

export const DEFAULT_COLUMN_2: FooterColumn = {
  title: "Menu",
  links: [
    {
      text: "Apparel",
      url: `${SWAGMAGIC_URL}/pk/en/stores/${PROMOTIONAL_STORE_SLUG}/${PROMOTIONAL_STORE_NUMBER}/apparel`,
    },
    {
      text: "Drinkware",
      url: `${SWAGMAGIC_URL}/pk/en/stores/${PROMOTIONAL_STORE_SLUG}/${PROMOTIONAL_STORE_NUMBER}/drinkware`,
    },
    {
      text: "Office Accessories",
      url: `${SWAGMAGIC_URL}/pk/en/stores/${PROMOTIONAL_STORE_SLUG}/${PROMOTIONAL_STORE_NUMBER}/all-products`,
    },
    {
      text: "Bags and Totes",
      url: `${SWAGMAGIC_URL}/pk/en/stores/${PROMOTIONAL_STORE_SLUG}/${PROMOTIONAL_STORE_NUMBER}/bags`,
    },
    {
      text: "Tech",
      url: `${SWAGMAGIC_URL}/pk/en/stores/${PROMOTIONAL_STORE_SLUG}/${PROMOTIONAL_STORE_NUMBER}/all-products`,
    },
    {
      text: "Others",
      url: `${SWAGMAGIC_URL}/pk/en/stores/${PROMOTIONAL_STORE_SLUG}/${PROMOTIONAL_STORE_NUMBER}/all-products`,
    },
  ],
};

export const DEFAULT_COLUMN_3: FooterColumn = {
  title: "Other",
  links: [
    { text: "The Story", url: `${SWAGMAGIC_URL}/company` },
    { text: "Use Cases", url: `${SWAGMAGIC_URL}/use-cases` },
    {
      text: "Downloads",
      url: "https://www.bystadium.com/resources",
    },
    {
      text: "Press",
      url: "https://www.snackmagic.com/press",
    },
    { text: "Blogs", url: `${SWAGMAGIC_URL}/blog` },
    {
      text: "For CPG Brands",
      url: "https://www.cpgpulse.com",
    },
    {
      text: "Careers",
      url: "https://www.bystadium.com/careers",
    },
  ],
};

export const DEFAULT_SOCIAL_LINKS: SocialLink[] = [
  {
    platform: "LinkedIn",
    url: "https://www.linkedin.com/company/bystadium",
    iconUrl: "https://cdn.bystadium.com/plasmic/static-slices/footer-linkedin-icon.svg",
  },
  {
    platform: "YouTube",
    url: "https://www.youtube.com/@bystadium",
    iconUrl: "https://cdn.bystadium.com/plasmic/static-slices/footer-youtube-icon.svg",
  },
  {
    platform: "Instagram",
    url: "https://www.instagram.com/bystadium",
    iconUrl: "https://cdn.bystadium.com/plasmic/static-slices/footer-instagram-icon.svg",
  },
  {
    platform: "Facebook",
    url: "https://www.facebook.com/swagmagicco",
    iconUrl: "https://cdn.bystadium.com/plasmic/static-slices/footer-facebook-icon.svg",
  },
];

export const DEFAULT_LEGAL_LINKS: FooterLink[] = [
  {
    text: "Terms",
    url: "https://www.bystadium.com/terms-of-use",
  },
  {
    text: "Privacy Policy",
    url: "https://www.bystadium.com/privacy-policy",
  },
];

export interface LogoData {
  logo?: { url: string };
  stadiumLogo?: { url: string };
  logoUrl?: string;
  logoAlt?: string;
}

export const FOOTER_DEFAULTS: { logo: LogoData; [key: string]: any } = {
  logo: {
    logo: { url: "https://cdn.bystadium.com/plasmic/static-slices/swagmagic-logo.png" },
    stadiumLogo: { url: "" },
    logoUrl: "",
    logoAlt: "Swagmagic",
  },
  watermark1920Image: { url: "" },
  newsLetter: {
    newsletterTitle: "Subscribe to our Newsletter",
    newsletterPlaceholder: "YOUR EMAIL ADDRESS",
    newsletterButtonAriaLabel: "Subscribe",
  },
  footerCta: {
    ctaText: "VIEW CATALOG",
    ctaUrl: `https://www.swagmagic.com/pk/en/stores/${PROMOTIONAL_STORE_SLUG}/${PROMOTIONAL_STORE_NUMBER}/all-products`,
  },
  column1: DEFAULT_COLUMN_1,
  column2: DEFAULT_COLUMN_2,
  column3: DEFAULT_COLUMN_3,
  socialLinks: DEFAULT_SOCIAL_LINKS,
  copyrightText: "Swagmagic - All rights reserved ©",
  legalLinks: DEFAULT_LEGAL_LINKS,
};

export const WATERMARK_URLS = {
  desktop: "https://cdn.bystadium.com/plasmic/static-slices/swagmagic-desktop.png",
} as const;

export const hasColumnData = (column?: FooterColumn) =>
  column?.title &&
  column?.links?.some((link) => link.text && (link.url || link.linkType || link.action));
